import { useParams, Link, useLocation } from 'react-router-dom'
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { Home, Eye, Clock, AlertCircle } from 'lucide-react'
import { ModelViewer, resolveModelSource } from '@/components/ModelViewer'
import { useSharedModel } from '@/hooks/useShareLinks'
import type { ShowcaseAnimation } from '@/types/camera'
import { migrateCameraState, cameraStateToQuaternion } from '@/utils/cameraConversion'
import { useCameraAnimation } from '@/utils/cameraAnimation'
import { XRControls } from '@/components/XRControls'
import { useXR } from '@/hooks/useXR'
import { ARDebugInfo } from '@/components/ARDebugInfo'

export function ShareViewer() {
  const { shareToken } = useParams<{ shareToken: string }>()
  const location = useLocation()
  const { data, isLoading, error } = useSharedModel(shareToken || '')
  const viewerCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const [, setShowXRControls] = useState(false)
  
  // Check if debug mode is enabled
  const showDebug = location.search.includes('debug=true')
  
  // Add debug log
  const addDebugLog = (message: string) => {
    console.log(`[ShareViewer] ${message}`)
  }
  
  const xr = useXR({ 
    enableFallback: true,
    onDebugLog: showDebug ? addDebugLog : undefined
  })
  
  // Routing (single / ply / sequence / clipgstream) + URL derivation all live
  // in resolveModelSource; ModelViewer picks the right viewer from this.
  // Must be before any conditional returns (hooks rule).
  const source = useMemo(
    () => (data?.model ? resolveModelSource(data.model) : null),
    [data?.model?.id, data?.model?.file_path, data?.model?.metadata],
  )
  const modelUrl = source?.modelUrl ?? ''

  const { defaultCamera, modelTransform, autoFocus, xrEnabled } = useMemo(() => {
    if (!data?.model?.metadata) {
      return { defaultCamera: undefined, modelTransform: undefined, autoFocus: true, xrEnabled: data?.model?.xr_enabled ?? false }
    }

    try {
      const metadata = typeof data.model.metadata === 'object'
        ? data.model.metadata
        : (typeof data.model.metadata === 'string'
          ? JSON.parse(data.model.metadata)
          : {})

      const camera = metadata.defaultCamera ? migrateCameraState(metadata.defaultCamera) : undefined

      return {
        defaultCamera: camera,
        modelTransform: metadata.modelTransform,
        autoFocus: !camera,
        xrEnabled: data?.model?.xr_enabled ?? false
      }
    } catch (e) {
      console.error('[ShareViewer] Failed to parse metadata:', e)
      return { defaultCamera: undefined, modelTransform: undefined, autoFocus: true, xrEnabled: data?.model?.xr_enabled ?? false }
    }
  }, [data?.model?.metadata, data?.model?.xr_enabled])

  // Stable tuple for DualgsViewer's external camera sync — recomputing each
  // render would re-fire the effect and override the user's drag.
  const dualgsCameraProps = useMemo(() => {
    if (!defaultCamera) return null
    return cameraStateToQuaternion(defaultCamera)
  }, [defaultCamera])

  // Showcase animation from metadata (with keyframe migration to quaternion form)
  const showcaseAnimation = useMemo<ShowcaseAnimation | undefined>(() => {
    if (!data?.model?.metadata) return undefined
    let meta: any = data.model.metadata
    if (typeof meta === 'string') {
      try { meta = JSON.parse(meta) } catch { return undefined }
    }
    const sa = meta?.showcaseAnimation
    if (!sa?.keyframes?.length) return undefined
    return {
      ...sa,
      // Share viewer always loops, regardless of the admin's saved setting.
      loop: true,
      keyframes: sa.keyframes.map((kf: any) => ({
        ...kf,
        camera: migrateCameraState(kf.camera)
      }))
    }
  }, [data?.model?.metadata])

  const animationEnabled = !!showcaseAnimation
  // Drive both viewers from one external hook so loop/interrupt behavior is
  // unified. SplatViewer's internal pauseAnimationOnInteraction triggers on
  // mere hover, which makes animations look like they don't loop — bypass it.
  const useExternalAnimation = animationEnabled
  const {
    currentCamera: animatedCamera,
    currentTime,
    play: playAnimation,
    pause: pauseAnimation,
    reset: resetAnimation,
  } = useCameraAnimation(
    showcaseAnimation?.keyframes ?? [],
    showcaseAnimation?.duration ?? 0,
    showcaseAnimation?.loop ?? false,
    useExternalAnimation
  )

  // Synchronous gate so the next animation rAF can't race a state update; the
  // state mirror is what triggers the re-render that drops the camera prop.
  const userInterruptedRef = useRef(false)
  const [userInterrupted, setUserInterrupted] = useState(false)

  // Reset whenever the shared model changes. The hook seeds isPlaying from
  // `enabled` only at mount, so we explicitly reset + play.
  useEffect(() => {
    userInterruptedRef.current = false
    setUserInterrupted(false)
    if (useExternalAnimation && data?.model) {
      resetAnimation()
      playAnimation()
    }
    // resetAnimation/playAnimation are stable useCallbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.model?.id, useExternalAnimation])

  // Pointer-down / wheel fires before OrbitControls processes the input, so we
  // can flip state in the same render the user's first move lands in.
  const handleViewerInteract = useCallback(() => {
    if (userInterruptedRef.current) return
    userInterruptedRef.current = true
    if (useExternalAnimation) pauseAnimation()
    setUserInterrupted(true)
  }, [useExternalAnimation, pauseAnimation])

  // Animated camera tuple — keyed on currentTime so paused state yields a stable
  // reference (viewer effect won't re-fire and overwrite the user's drag).
  const animatedCameraProps = useMemo(() => {
    if (!useExternalAnimation || userInterrupted) return null
    return cameraStateToQuaternion(animatedCamera)
    // animatedCamera identity changes each render; currentTime is the real driver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExternalAnimation, userInterrupted, currentTime])

  // After interaction we pass nothing so the viewer keeps the user's last view.
  // Otherwise: animation > static default.
  const activeCameraProps = userInterrupted
    ? null
    : (animatedCameraProps ?? dualgsCameraProps)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Loading model...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-white text-center mb-2">
            Share Link Error
          </h2>
          <p className="text-gray-400 text-center mb-6">
            {error instanceof Error ? error.message : 'This share link is invalid or has expired.'}
          </p>
          <Link
            to="/"
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            <Home size={16} />
            Go to Homepage
          </Link>
        </div>
      </div>
    )
  }

  const { model, shareInfo } = data

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Home size={20} />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white">{model.name}</h1>
                {model.description && (
                  <p className="text-sm text-gray-400">{model.description}</p>
                )}
              </div>
            </div>
            
            {/* Share Info and XR Controls */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm text-gray-400">
                {shareInfo.maxViews && (
                  <div className="flex items-center gap-1">
                    <Eye size={14} />
                    <span>{shareInfo.viewCount} / {shareInfo.maxViews} views</span>
                  </div>
                )}
                {shareInfo.expiresAt && (
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>Expires {new Date(shareInfo.expiresAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              
              {/* XR Controls - only show if xr_enabled (the XR service only
                  reads .splatv, so never for a .4dv) */}
              {!xr.state.isActive && xrEnabled && source?.kind !== 'fourdv' && (
                <XRControls
                  onEnterVR={() => {
                    if (viewerCanvasRef.current) {
                      xr.enterVR(viewerCanvasRef.current, modelUrl)
                    }
                  }}
                  onEnterAR={() => {
                    if (viewerCanvasRef.current) {
                      xr.enterAR(viewerCanvasRef.current, modelUrl)
                    }
                  }}
                  onExitXR={() => xr.exitXR()}
                  xrState={xr.state}
                />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Viewer */}
      <div
        className="flex-1 relative"
        onPointerDownCapture={handleViewerInteract}
        onMouseDownCapture={handleViewerInteract}
        onTouchStartCapture={handleViewerInteract}
        onWheelCapture={handleViewerInteract}
      >
        {source && (
          <div className="absolute inset-0">
            <ModelViewer
              source={source}
              className="w-full h-full"
              cameraPosition={activeCameraProps?.position ?? null}
              cameraQuaternion={activeCameraProps?.quaternion ?? null}
              onCameraChange={handleViewerInteract}
              onCanvasReady={(canvas) => {
                viewerCanvasRef.current = canvas
              }}
              dualgs={{
                showUi: false,
                showTimeline: !!source.sequenceInfo,
                enableXR: true,
              }}
              splat={{
                controls: true,
                quality: 'high',
                defaultCamera,
                modelTransform,
                autoFocus: autoFocus && !animationEnabled,
                xrEnabled,
                onXRSessionStart: () => setShowXRControls(false),
                onXRSessionEnd: () => setShowXRControls(true),
                onLoad: () => {
                  // External hook drives the animation; apply the static default
                  // here only when there's no animation.
                  if (animationEnabled || !defaultCamera) return
                  setTimeout(() => {
                    const canvas = viewerCanvasRef.current
                    if (canvas) {
                      const renderer = (canvas as any)?.__splatRenderer
                      if (renderer && 'setCameraWithQuaternion' in renderer) {
                        const { position, quaternion } = cameraStateToQuaternion(defaultCamera)
                        renderer.setCameraWithQuaternion(position, quaternion)
                        // Also emit camera change event to update axis gizmo
                        if (renderer.emit) {
                          renderer.emit('cameraChange', position, quaternion)
                        }
                      }
                    }
                  }, 150)
                },
              }}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 border-t border-gray-700 py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <div className="text-gray-400">
              Powered by{' '}
              <Link to="/" className="text-primary-400 hover:text-primary-300">
                4DGS Moonshine
              </Link>
            </div>
            <div className="flex items-center gap-6 text-gray-500">
              {/* <span>Size: {(model.file_size / (1024 * 1024)).toFixed(1)} MB</span>
              {model.duration && <span>Duration: {Math.round(model.duration * 10) / 10}s</span>}
              {model.vertices && <span>Vertices: {model.vertices.toLocaleString()}</span>} */}
            </div>
          </div>
        </div>
      </footer>
      
      {/* AR Debug Info */}
      {showDebug && <ARDebugInfo xrState={xr.state} show={true} />}
    </div>
  )
}